
























import _ from 'lodash'
import { Mixins } from 'vue-mixin-decorator'
import { Component, Emit, Prop, Watch } from 'vue-property-decorator'

import { log } from '@/log'
import { ApiNoveltyCampaign, NoveltyRule } from '@/models/api/campaign/noveltyCampaign'
import { NoveltyCampaign } from '@/models/novelty/novelty'
import { CampaignConstraint } from '@/usecases/novelty/campaign-constraint'
import { CampaignSummary } from '@/usecases/novelty/campaign-summary'
import { convertToUseCaseState } from '@spa/components/organisms/cart/novelty/util'
import { PluginMixin } from '@spa/mixins/plugin'
import { CartItem } from '@spa/store/modules/cart/types'
import NoveltyCampaignSummarySection from './NoveltyCampaignSummarySection.vue'
import NoveltyFkuSection from './NoveltyFkuSection.vue'
import NoveltyItem from './NoveltyItem.vue'
import { NoveltyEntity, Selection } from './selection'

@Component({
  components: {
    NoveltyCampaignSummarySection,
    NoveltyItem,
    NoveltyFkuSection,
  },
})
export default class NoveltyCampaignSection extends Mixins<PluginMixin>(PluginMixin) {
  @Prop({ default: false })
  readOnly: boolean

  @Prop({ required: true })
  noveltyCampaign: NoveltyCampaign

  @Prop({ required: true })
  noveltiesInCart: CartItem[]

  @Prop({ required: true })
  noveltySummary: CampaignSummary

  entities: NoveltyEntity[] = this.makeInitialEntities()

  get campaign(): ApiNoveltyCampaign {
    return this.noveltyCampaign.campaign
  }

  get fkuIds(): string[] {
    return _.uniq(
      this.entities.map(entity => entity.item.fkuId)
    )
  }
  get basePriceToSelectNovelty(): number {
    return this.noveltySummary.basePriceToSelectNovelty
  }

  created() {
    this.emitEntities(this.entities)
  }

  createConstraint(entities: NoveltyEntity[]): CampaignConstraint {
    return new CampaignConstraint(
      this.noveltySummary.useNoveltiesAsSelection(convertToUseCaseState(entities))
    )
  }

  findRules(noveltyFkuId: string): NoveltyRule | null {
    return this.campaign.rules.find(rule => rule.id === noveltyFkuId) || null
  }

  filterFkuEntities(fkuId: string): NoveltyEntity[] {
    return this.entities.filter(e => e.item.fkuId === fkuId)
  }

  handleSelect(val: Selection) {
    const updated = this.makeNewStates(val)

    const constraint = this.createConstraint(updated)

    if (!constraint.isValid) {
      this.emitConstraintError(new Error('Novelty is not updated with invalid data'))
    }

    this.entities = updated

    const updatedItem = updated.find(nov => nov.item.skuId === val.skuId)
    if (!updatedItem) {
      log.warn(`novelty corresponding to selected not found, so ignored`, val)

      return
    }
    const index = this.entities.indexOf(updatedItem)

    this.emitEntitiesWithChangedIndex(this.entities, index)
  }

  @Emit('init')
  emitEntities(_allItems: NoveltyEntity[]): void {
    // Do nothing
  }

  @Emit('change')
  emitEntitiesWithChangedIndex(_allItems: NoveltyEntity[], _changedIndex: number): void {
    // Do nothing
  }

  @Emit('constraint-error')
  emitConstraintError(_error: Error): void {
    // Do nothing
  }

  @Watch('basePriceToSelectNovelty', { immediate: true })
  resetNoveltySelectionsIfInvalid(): void {
    const constraint = this.createConstraint(this.entities)
    if (!constraint.isValid) {
      this.emitResetNoveltySelection(new Error('Novelty selections were reset because of invalid selections.'))
      this.entities = this.setNoNoveltyStates()
    }
  }

  @Emit('reset-novelty-selection')
  emitResetNoveltySelection(_error: Error): void {
    // Do nothing
  }

  /**
   * Create and flatten SKU-grained selection states (including non-selected) with their detailed product data.
   */
  private makeInitialEntities(): NoveltyEntity[] {
    return this.noveltyCampaign.noveltyList.map((novelty) => {
      const cart = this.noveltiesInCart.find(c => c.skuId === novelty.skuId)

      return {
        state: {
          count: cart ? cart.unitCount : 0,
        },
        item: novelty,
      }
    })
  }

  private makeNewStates(val: Selection): NoveltyEntity[] {
    return this.entities.map(entity =>
      this.mergeSelectedWithEntity(val, entity)
    )
  }
  private mergeSelectedWithEntity(val: Selection, entity: NoveltyEntity): NoveltyEntity {
    if (entity.item.skuId !== val.skuId) {
      return entity
    }

    return {
      ...entity,
      state: {
        count: val.count,
      },
    }
  }

  private setNoNoveltyStates(): NoveltyEntity[] {
    return this.entities.map((entity) => {
      return {
        ...entity,
        state: {
          count: 0,
        },
      }
    })
  }
}
